




































































































































import { computed, defineComponent, getCurrentInstance, ref, toRef } from "@vue/composition-api";
import { FundraisingGoal } from "@scrile/api-provider/dist/api/FundraisingGoalsProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import useRole from "@/hooks/useRole";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString } from "@/lib/moneyHelpers";
import { timeAgo } from "@/lib/time";
import config from "@/config";
import { AuthUserAuthorities, BaseMenuItem, LoginSignUpModalData } from "@/types";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIconButton from "@/components/BaseIconButton";
import BaseMenu from "@/components/BaseMenu";
import FundraisingGoalCard from "@/components/FundraisingGoal/FundraisingGoalCard";
import LinkedUsersModal from "@/components/LinkedUsersModal";
import UnfollowConfirm from "@/components/UnfollowConfirm";
import UserFollowSubscribeModal from "@/components/UserFollowSubscribeModal";
import BannerFundraisingGoals from "../BannerFundraisingGoals";
import ProfileHeader from "../ProfileHeader";
import ProfileStatistic from "../ProfileStatistic";
import QuestionaryInfoPrinter from "../QuestionaryInfoPrinter";

export default defineComponent({
  name: "ProfileInfoView",
  components: {
    FundraisingGoalCard,
    BaseIconButton,
    UnfollowConfirm,
    QuestionaryInfoPrinter,
    ProfileHeader,
    BaseButton,
    BaseMenu,
    BaseIcon,
    BaseConfirm,
    ProfileStatistic,
    UserFollowSubscribeModal,
    LinkedUsersModal,
    BannerFundraisingGoals,
  },
  props: {
    user: { type: Object as () => UserPublicData, required: true },
    questionaryInfo: { type: Object as () => Record<string, any>, default: () => ({}) },
    isFollowing: { type: Boolean, required: true },
    isReceivingNotifications: { type: Boolean, default: false },
    isBlocked: { type: Boolean, required: false },
    isAuthor: { type: Boolean, required: true },
    subscriptionLevels: { type: Array as () => SubscriptionLevel[], required: true },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
    activeSubscriptions: { type: Array as () => Subscription[], required: true },
    hasContentCount: { type: Boolean, required: true },
    verifiedIconSize: { type: [Number, String], required: false, default: 16 },
    isBookingAvailable: { type: Boolean, required: true },
    activeGoal: { type: Object as () => FundraisingGoal | null },
    showGoalRewardsButton: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const showFollowSubscribe = ref(false);
    const showUnfollowConfirm = ref(false);
    const showSwitchAccount = ref(false);

    const screenName = computed(() => props.user.screenName);
    const username = computed(() => props.user.username);
    const verified = computed(() => props.user.verified);

    const hasSubscriptionLevels = computed(() => props.subscriptionLevels.length > 0);

    const { isCreator: isUserCreator } = useRole(toRef(props, "user"));
    const { authorities, authUser, updateUser, isAdmin, isCreator } = useAuthUser();

    const canEditInfo = computed(() => {
      const requiredAuthorities: AuthUserAuthorities = props.isAuthor
        ? ["updateMyQuestionaryInfo", "updateMyPersonalInfo", "updateMyFile"]
        : ["updateAnyQuestionaryInfo", "updateAnyPersonalInfo", "updateAnyFile"];
      return checkAuthorities(authorities.value, requiredAuthorities);
    });

    const canUpdateGoal = computed(() => checkAuthorities(authorities.value, ["updateMyFundraisingGoal"]));

    const canSwitchAccount = computed(
      () => config.featureMultipleAccounts && checkAuthorities(authorities.value, ["getMyLinkedUser"]) && props.isAuthor
    );

    const hasActiveSubscription = computed(() => props.activeSubscriptions.length > 0);
    const subscribeButtonText = computed(() => {
      if (hasActiveSubscription.value) {
        return $t("Subscribed for {0}", [$t("{0}/mo", [formatMoneyString(props.activeSubscriptions[0].amount)])]);
      }
      const availableSubscriptions = props.subscriptionLevels.filter(i => (i.contentCount ?? 0) > 0);
      const cheapestAvailableSubscription = availableSubscriptions.reduce<SubscriptionLevel | null>((out, item) => {
        if (!out) {
          return item;
        }
        if (!item.pricePackage) {
          return out;
        }
        if (item.pricePackage.amount < (out.pricePackage?.amount ?? Infinity)) {
          return item;
        }
        return out;
      }, null);
      if (!cheapestAvailableSubscription || !cheapestAvailableSubscription.pricePackage) {
        return $t("No available subscriptions");
      }
      return $t("Subscribe from {0}", [
        $t("{0}/mo", [formatMoneyString(cheapestAvailableSubscription.pricePackage.amount)]),
      ]);
    });

    const isModeVisibility = computed(() => isCreator.value && config.featureInvisibleMode && props.isAuthor);

    const lastOnline = computed(() => {
      if (props.user.onlineStatus?.isOnline && !props.user.onlineStatus?.invisible) return $t("Online");
      if (props.user.onlineStatus?.isOnline && props.user.onlineStatus?.invisible && isModeVisibility.value)
        return $t("Invisible");
      if (!props.user.onlineStatus?.lastOnlineTime) return "";
      return $t(`Last visited {0}`, [timeAgo(props.user.onlineStatus.lastOnlineTime, { addSuffix: true })]);
    });

    const activeUserMode = ref(lastOnline.value);

    const userModes = computed<BaseMenuItem[]>(() => [
      {
        text: $t("Online"),
        name: "Online",
        class: activeUserMode.value === "Online" ? "fw-6oo" : "",
      },
      {
        text: $t("Invisible"),
        name: "Invisible",
        class: activeUserMode.value === "Invisible" ? "fw-6oo" : "",
      },
    ]);

    const isDisabled = computed(() => props.user.disabled ?? true);
    const showActionButton = computed(() => {
      return !isDisabled.value && !props.isBlocked && !props.isAuthor;
    });
    const showSubscribeButton = computed(() => {
      return showActionButton.value && hasSubscriptionLevels.value && props.hasContentCount;
    });
    const showAppointmentButton = computed(() => {
      return showActionButton.value && props.isBookingAvailable;
    });

    const onSignUpSuccess = async () => {
      emit("onFollowUpdate");
    };
    const loginSignUpModalData: LoginSignUpModalData = { onSignUpSuccess };
    const onUnfollowClick = () => {
      onSignUpSuccess();
      showUnfollowConfirm.value = false;
    };
    const onFollowingClick = useAuthorisedOnly(() => onSignUpSuccess(), loginSignUpModalData);
    const onSubscribingClick = useAuthorisedOnly(() => (showFollowSubscribe.value = true), loginSignUpModalData);

    const vm = getCurrentInstance();
    const sendMessage = useAuthorisedOnly(async () => {
      await vm?.$router.push({ name: "messages", params: { userId: props.user.id } });
    }, loginSignUpModalData);

    const handleFollow = () => {
      if (!props.isFollowing) {
        onFollowingClick();
      } else {
        showUnfollowConfirm.value = true;
      }
    };

    const onSelectMode = (value: string) => {
      activeUserMode.value = value;
      const invisible = value === "Invisible";
      providers.OnlineStatusProvider.updateVisibility({ data: { invisible } });
      authUser.value && updateUser(authUser.value);
      emit("updateUser");
    };

    return {
      username,
      verified,
      lastOnline,
      showFollowSubscribe,
      hasActiveSubscription,
      isDisabled,
      screenName,
      canEditInfo,
      showUnfollowConfirm,
      showActionButton,
      showSubscribeButton,
      showAppointmentButton,
      sendMessage,
      subscribeButtonText,
      onFollowingClick,
      onSubscribingClick,
      userModes,
      activeUserMode,
      isModeVisibility,
      isAdmin,
      canSwitchAccount,
      showSwitchAccount,
      isUserCreator,
      canUpdateGoal,
      handleFollow,
      onUnfollowClick,
      onSelectMode,
    };
  },
});
